import React, { useState, useEffect } from 'react';
import { FiEdit2, FiX, FiPlus, FiMinus, FiSave, FiRefreshCw } from 'react-icons/fi';
import api from '../utils/api';
import toast from 'react-hot-toast';

const TableBillUpdateModal = ({ table, show, onHide, onSuccess }) => {

    const [formData, setFormData] = useState({
    updateReason: '',
    updateType: 'item_modification',
    complimentary: '',
    items: [],
    pricing: {
      subtotal: 0,
      gstAmount: 0,
      discount: 0,
      total: 0
    },
    discount: {
      amount: 0,
      value: 0,
      type: 'percentage'
    },
    gstNumber: '',
    isMc: false,
    cashOnly: false,
    payment: {
      cashAmount: '0',
      onlineAmount: '0'
    },
    vendorDiscount: {
      discountType: 'flat',
      discountValue: 0,
      discountAmount: 0,
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [originalData, setOriginalData] = useState(null);

  // Initialize data when component mounts or table changes
  useEffect(() => {
    if (table && show) {
      initializeTableData();
    }
  }, [table, show]);

  const initializeTableData = () => {
    // Aggregate all orders from the table
    const aggregatedData = aggregateTableData(table);
    setFormData({
      updateReason: '',
      updateType: 'item_modification',
      complimentary: aggregatedData.complimentary,
      items: aggregatedData.items,
      orders: aggregatedData.orders,
      pricing: aggregatedData.pricing,
      discount: {
        value: aggregatedData?.vendorDiscount?.discountValue,
        amount: aggregatedData?.vendorDiscount?.discountAmount,
        type: aggregatedData?.vendorDiscount?.discountType || 'percentage'
      },
      gstNumber: aggregatedData.gstNumber,
      isMc: aggregatedData.isMc,
      cashOnly: aggregatedData.cashOnly,
      payment: {
        cashAmount: aggregatedData.pricing?.cashAmount?.toString() || '0',
        onlineAmount: aggregatedData.pricing?.onlineAmount?.toString() || '0'
      },
      vendorDiscount: {
        discountType: aggregatedData?.vendorDiscount?.discountType,
        discountValue: aggregatedData?.vendorDiscount?.discountValue,
        discountAmount: aggregatedData?.vendorDiscount?.discountAmount,
      }
    });

    setOriginalData(aggregatedData);
  };

  const aggregateTableData = (tableData) => {
    if (!tableData.orders || tableData.orders.length === 0) {
      return {
        items: [],
        orders: [],
        pricing: { subtotal: 0, gstAmount: 0, discount: 0, total: 0 },
        gstNumber: '',
        isMc: false,
        complimentary: '',
        cashOnly: false,
        paymentDetails: {},
        vendorDiscount: {
          discountType: "flat",
          discountValue: 100,
          discountAmount: 100
        }
      };
    }

    // Keep order-wise items instead of aggregating
    let onlineAmount= 0;
    let cashAmount = 0;
    const ordersWithItems = [];
    
    tableData.orders.forEach(orderRef => {
      const order = orderRef.orderId;
      if (order && order.items) {
        // Process each order separately
        const orderItems = order.items.map(item => ({
          menuItemId: item.menuItemId,
          name: item.menuItemId?.name || item.name,
          price: item.price,
          quantity: item.quantity,
          subtotal: item.price * item.quantity,
          orderNumber: order.orderNumber,
          orderId: order._id
        }));
        
        ordersWithItems.push({
          orderNumber: order.orderNumber,
          orderId: order._id,
          items: orderItems
        });
        
        onlineAmount += Number(order?.paymentDetails?.onlineAmount)
        cashAmount += Number(order?.paymentDetails?.cashAmount)
      }
    });

    // Flatten all items for calculations but keep order info
    const allItems = ordersWithItems.flatMap(order => order.items);

    return {
      items: allItems,
      orders: ordersWithItems,
      pricing: {
        subtotal: tableData?.totalSubtotal,
        gstAmount: tableData?.totalGst,
        discount: tableData?.totalDiscount,
        total: tableData?.totalAmount,
        onlineAmount,
        cashAmount
      },
      gstNumber: tableData.orders[0]?.orderId?.gstNumber || '',
      isMc: tableData.orders[0]?.orderId?.isMc || false,
      complimentary: tableData.orders[0]?.orderId?.complimentary || '',
      cashOnly: false,
      paymentDetails: tableData.orders[0]?.orderId?.paymentDetails || {},
      vendorDiscount: {
        discountType: tableData?.vendorDiscount?.discountType,
        discountValue: tableData?.vendorDiscount?.discountValue,
        discountAmount: tableData?.vendorDiscount?.discountAmount
      }
    };
  };

  // Calculate totals when items or discount changes
  useEffect(() => {
    if (formData.items.length > 0) {
      calculateTotals();
    }
  }, [formData.discount]);

  const calculateTotals = () => {
    // Calculate subtotal from modified items
    let subtotal = formData.items.reduce((sum, item) => {
      return sum + (item.subtotal || (item.price * item.quantity));
    }, 0);

    // Add original GST amount (will be recalculated by backend)
    const originalGstAmount = formData?.pricing?.gstAmount || 0;
    const subtotalWithGst = subtotal + originalGstAmount;
    
    const discountAmount = formData.discount.type === 'percentage' 
      ? (subtotalWithGst * formData.discount.amount) / 100 
      : formData.discount.amount;

    const afterDiscount = Math.max(0, subtotalWithGst - discountAmount);
    
    // GST will be calculated by backend, frontend only handles discount
    const total = afterDiscount;

    setFormData(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        subtotal,
        gstAmount: originalGstAmount,
        discount: discountAmount,
        total,
      },
      payment: {
        cashAmount: prev.payment.cashAmount,
        onlineAmount: total - (parseFloat(prev.payment.cashAmount) || 0)
      },
    }));
  };

  const handleDiscountChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      discount: {
        ...prev.discount,
        [field]: field === 'amount' ? parseFloat(value) || 0 : value
      }
    }));
  };

  // Item modification functions (similar to BillUpdateRequestModal)
  const updateItemQuantity = (index, newQuantity) => {
    // Handle both string and number inputs
    const stringValue = String(newQuantity);
    // Allow only positive integers and empty string
    const cleanValue = stringValue.replace(/[^0-9]/g, '');
    const newQuantityNum = cleanValue === '' ? 0 : parseInt(cleanValue) || 0;
    
    setFormData(prev => {
      const newItems = [...prev.items];
      const item = newItems[index];
      
      item.quantity = newQuantityNum;
      item.subtotal = item.price * newQuantityNum;

      let gstAmount = 0;
      let subtotal = 0;

      for(let item of newItems){
        let price = item.price;
        let quantity = item.quantity;
        let isGSTInclude = item?.menuItemId?.categoryId?.isGSTInclude;
        let gstPercentage = item?.menuItemId?.categoryId?.gstPercentage;

        let itemBaseTotal = 0;
        let itemGSTTotal = 0;

        if (isGSTInclude) {
          const basePrice = price * 100 / (100 + gstPercentage);
          itemBaseTotal = basePrice * quantity;
          itemGSTTotal = (price - basePrice) * quantity;
        } else {
          itemBaseTotal = price * quantity;
          itemGSTTotal = (itemBaseTotal * gstPercentage) / 100;
        }

        subtotal += itemBaseTotal;
        gstAmount += itemGSTTotal;
      }

      // console.log(newItems,"newItemsnewItems",subtotal,gstAmount)
      
      return {
        ...prev,
        items: newItems,
        pricing: {
          ...prev.pricing,
          subtotal: subtotal,
          gstAmount: gstAmount,
          total: gstAmount + subtotal
        },
        payment: {
          cashAmount: 0,
          onlineAmount: gstAmount + subtotal - formData.discount.amount
        }
      };
    });
  };

  const updateItemPrice = (index, newPrice) => {
    // Allow only numbers and decimal point, format to 2 decimal places
    let cleanValue = newPrice.replace(/[^0-9.]/g, '');
    
    // Prevent multiple decimal points
    const parts = cleanValue.split('.');
    if (parts.length > 2) {
      cleanValue = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Limit to 2 decimal places
    if (parts[1] && parts[1].length > 2) {
      cleanValue = parts[0] + '.' + parts[1].substring(0, 2);
    }
    
    const newPriceNum = cleanValue === '' ? 0 : parseFloat(cleanValue) || 0;
    
    setFormData(prev => {
      const newItems = [...prev.items];
      const item = newItems[index];
      
      item.price = newPriceNum;
      item.subtotal = newPriceNum * item.quantity;
      
      return {
        ...prev,
        items: newItems
      };
    });
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate update reason
    if (!formData.updateReason.trim()) {
      toast.error('Please provide a reason for the bill update');
      return;
    }

    // Validate that there are items in the bill
    if (formData.items.length === 0) {
      toast.error('Bill must have at least one item');
      return;
    }

    // Validate that all items have valid quantity and price
    const hasInvalidItems = formData.items.some(item => 
      item.quantity <= 0 || 
      item.price < 0 || 
      item.quantity === '' || 
      item.price === '' ||
      isNaN(item.quantity) || 
      isNaN(item.price)
    );
    if (hasInvalidItems) {
      toast.error('All items must have valid quantity and price (greater than 0)');
      return;
    }

    // Validate discount amount
    if (formData.discount.amount < 0) {
      toast.error('Discount amount cannot be negative');
      return;
    }

    // Validate payment amounts
    const cashAmount = parseFloat(formData.payment.cashAmount) || 0;
    const onlineAmount = parseFloat(formData.payment.onlineAmount) || 0;
    const totalAmount = formData.pricing.total;

    if (!formData.isMc && (cashAmount < 0 || onlineAmount < 0)) {
      toast.error('Payment amounts cannot be negative');
      return;
    }
    console.log((cashAmount + onlineAmount),totalAmount,"totalAmounttotalAmount",formData)
    if (!formData.isMc && Math.abs((cashAmount + onlineAmount) - totalAmount) > 0.01) {
      toast.error('Payment amounts must equal the total amount');
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        items: formData.items,
        orders: formData.orders, // Send order-wise items structure
        pricing: formData.pricing,
        discount: formData.discount,
        updateReason: formData.updateReason,
        updateType: formData.updateType,
        complimentary: formData.complimentary,
        gstNumber: formData.gstNumber,
        isMc: formData.isMc,
        cashOnly: formData.cashOnly,
        payment: formData.payment,
        // Table context information
        isTableContext: true,
        tableId: table.tableId?._id,
        tableNumber: table.tableId?.tableNumber,
        orderIds: table.orders?.map(o => o.orderId._id)
      };

      const response = await api.post(`/vendor/tables/${table.tableId?._id}/bill-update-request`, requestData);
      
      toast.success('Table bill update request submitted successfully. Pending approval from vendor owner.');
      onSuccess(response.data.data);
      onHide();
    } catch (error) {
      console.error('Error submitting table bill update request:', error);
      toast.error(error.response?.data?.message || 'Failed to submit table bill update request');
    } finally {
      setLoading(false);
    }
  };


  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <FiEdit2 className="text-blue-600 text-xl" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Update Table Bill</h2>
              {/* <p className="text-sm text-gray-600">Table {table?.tableId?.tableNumber} • {table?.orders?.length || 0} Orders</p> */}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onHide}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Table Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">Table Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Table Number:</span>
                  <span className="ml-2 font-medium">{table?.tableId?.tableNumber}</span>
                </div>
                <div>
                  <span className="text-gray-600">Total Orders:</span>
                  <span className="ml-2 font-medium">{table?.orders?.length || 0}</span>
                </div>
                <div>
                  <span className="text-gray-600">Customer:</span>
                  <span className="ml-2 font-medium">{table?.customerId?.fullName || 'Guest'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Duration:</span>
                  <span className="ml-2 font-medium">
                    {table?.startTime ? Math.round((new Date() - new Date(table.startTime)) / 60000) + ' min' : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Update Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Update Reason *
              </label>
              <textarea
                value={formData.updateReason}
                onChange={(e) => handleInputChange('updateReason', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                placeholder="Reason for updating the table bill..."
                required
              />
            </div>

            {/* Items Section - Order-wise Display */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Bill Items (Order-wise)</h3>

              <div className="space-y-4">
                {formData.orders?.map((order, orderIndex) => (
                  <div key={order.orderId} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Order Header */}
                    <div className="bg-blue-50 px-4 py-2 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-blue-900">
                            Order #{order.orderNumber}
                          </span>
                          <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                            {order.items.length} items
                          </span>
                        </div>
                        <div className="text-sm font-medium text-blue-900">
                          ₹{order.items.reduce((sum, item) => sum + item.subtotal, 0).toFixed(2)}
                        </div>
                      </div>
                    </div>

                    {/* Order Items */}
                    <div className="p-4 space-y-2">
                      {order.items.map((item, itemIndex) => {
                        const globalIndex = formData.items.findIndex(
                          globalItem => globalItem.orderId === order.orderId && globalItem.name === item.name
                        );
                        
                        return (
                          <div key={`${order.orderId}-${itemIndex}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {item.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                Order #{order.orderNumber}
                              </div>
                            </div>
                            <div className="w-20">
                              <label className="block text-xs text-gray-600 mb-1">Qty</label>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => updateItemQuantity(globalIndex, Math.max(0, item.quantity - 1))}
                                  className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded"
                                  title="Decrease quantity"
                                >
                                  <FiMinus size={12} />
                                </button>
                                <input
                                  type="text"
                                  value={item.quantity}
                                  onChange={(e) => updateItemQuantity(globalIndex, e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-center"
                                  placeholder="0"
                                />
                                <button
                                  type="button"
                                  onClick={() => updateItemQuantity(globalIndex, item.quantity + 1)}
                                  className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded"
                                  title="Increase quantity"
                                >
                                  <FiPlus size={12} />
                                </button>
                              </div>
                            </div>
                            <div className="w-24">
                              <label className="block text-xs text-gray-600 mb-1">Price (₹)</label>
                              <input
                                type="text"
                                value={item.price}
                                disabled
                                onChange={(e) => updateItemPrice(globalIndex, e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                              />
                            </div>
                            <div className="w-24 text-right">
                              <label className="block text-xs text-gray-600 mb-1">Subtotal</label>
                              <div className="font-medium text-sm">
                                ₹{item.subtotal.toFixed(2)}
                              </div>
                            </div>
                            {/* <div className="w-8">
                              <button
                                type="button"
                                onClick={() => removeItem(globalIndex)}
                                className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                title="Remove item"
                              >
                                <FiX size={16} />
                              </button>
                            </div> */}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Discount Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Discount</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount Type
                  </label>
                  <select
                    value={formData.discount.type}
                    onChange={(e) => handleDiscountChange('type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="flat">Flat Amount (₹)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount {formData.discount.type === 'percentage' ? '(%)' : '(₹)'}
                  </label>
                  <input
                    type="text"
                    value={formData.discount.amount}
                    onChange={(e) => handleDiscountChange('amount', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={formData.discount.type === 'percentage' ? '0.00' : '0.00'}
                  />
                </div>
              </div>
            </div>

            {/* Pricing Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">₹{formData.pricing.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Discount:</span>
                  <span className="font-medium text-red-600">-₹{formData.pricing.discount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>Total:</span>
                  <span className="text-green-600">₹{formData.pricing.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Payment Options */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="flex flex-col sm:flex-row sm:items-center sm:justify-start gap-2 sm:gap-7 text-sm font-medium text-gray-700 mb-2">
                  <span>Cash Amount (₹)</span>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      checked={formData.cashOnly}
                      onChange={(e) => {
                        const cashOnlyChecked = e.target.checked;
                        handleInputChange('cashOnly', cashOnlyChecked);

                        if (cashOnlyChecked) {
                          let cashAmount = Number(formData.payment.cashAmount) + Number(formData.payment.onlineAmount);
                          handleInputChange('payment', { ...formData.payment, cashAmount: cashAmount.toString(), onlineAmount: '0' });
                        } else {
                          let onlineAmount = Number(formData.payment.cashAmount) + Number(formData.payment.onlineAmount);
                          handleInputChange('payment', { ...formData.payment, cashAmount: '0', onlineAmount: onlineAmount.toString() });
                        }
                      }}
                    />
                    <span>Cash Only</span>
                  </div>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={formData.payment.cashAmount}
                  disabled={formData.isMc || formData.cashOnly}
                  placeholder="Enter cash amount"
                  onChange={(e) => {
                    if (formData.isMc || formData.cashOnly) return;
                    let value = e.target.value.replace(/[^0-9.]/g, '');
                    const parts = value.split('.');
                    if (parts.length > 2) {
                      value = parts[0] + '.' + parts.slice(1).join('');
                    }

                    const billTotal = Number(formData.pricing.total);
                    const cashAmt = Number(value) || 0;
                    const remainingAmt = billTotal - cashAmt;
                    const onlineAmt = remainingAmt > 0 ? remainingAmt.toFixed(2) : '';

                    handleInputChange('payment', { ...formData.payment, cashAmount: value, onlineAmount: onlineAmt });
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Online Amount (₹)</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={formData.payment.onlineAmount}
                  disabled={formData.isMc || formData.cashOnly}
                  placeholder="Auto-filled or enter manually"
                  onChange={(e) => {
                    if (formData.isMc || formData.cashOnly) return;
                    let value = e.target.value.replace(/[^0-9.]/g, '');
                    const parts = value.split('.');
                    if (parts.length > 2) {
                      value = parts[0] + '.' + parts.slice(1).join('');
                    }

                    const billTotal = Number(formData.pricing.total);
                    const onlineAmt = Number(value) || 0;
                    const remainingAmt = billTotal - onlineAmt;
                    const cashAmt = remainingAmt > 0 ? remainingAmt.toFixed(2) : '';

                    handleInputChange('payment', { ...formData.payment, cashAmount: cashAmt, onlineAmount: value });
                  }}
                />
              </div>
            </div>

            
            {/* GST and MC Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">GST Number</label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={formData.isMc}
                  value={formData.gstNumber}
                  placeholder='Enter GST Number'
                  onChange={(e) => {
                    handleInputChange('gstNumber', e.target.value.toUpperCase());
                  }}
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                    checked={formData.isMc}
                    onChange={(e) => {
                      const isMcChecked = e.target.checked;
                      handleInputChange('isMc', isMcChecked);

                      if (isMcChecked) {
                        // When MC is checked, set all amounts to 0
                        handleInputChange('discount', { amount: 0, value:0, type: 'percentage' });
                        handleInputChange('payment', { cashAmount: '0', onlineAmount: '0' });
                        handleInputChange('complimentary', '');
                        handleInputChange('gstNumber', '');
                      } else {
                        // When MC is unchecked, reset to original values
                        handleInputChange('discount', { amount: 0, value: 0, type: 'percentage' });
                        handleInputChange('payment', { cashAmount: '0', onlineAmount: formData.pricing.total.toString() });
                        handleInputChange('complimentary', '');
                      }
                    }}
                  />
                  <span className="text-sm font-medium text-gray-700">Is Complimentary</span>
                </label>
              </div>
            </div>

            {/* Complimentary */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Complimentary</label>
              <textarea
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                rows="3"
                value={formData.complimentary}
                placeholder="Enter complimentary (if any)"
                onChange={(e) => {
                  handleInputChange('complimentary', e.target.value);
                }}
              />
            </div>

            {/* Payment Breakdown */}
            {(Number(formData.payment.cashAmount) > 0 || Number(formData.payment.onlineAmount) > 0) && (
              <div className="bg-gray-100 p-4 rounded-lg mb-6">
                <p className="font-semibold mb-2">Payment Breakdown:</p>
                {Number(formData.payment.cashAmount) > 0 && (
                  <p className="text-green-600 mb-1">💵 Cash: ₹{Number(formData.payment.cashAmount).toFixed(2)}</p>
                )}
                {Number(formData.payment.onlineAmount) > 0 && (
                  <p className="text-blue-600 mb-2">💳 Online: ₹{Number(formData.payment.onlineAmount).toFixed(2)}</p>
                )}
                <p className="font-bold">
                  Total Paid: ₹{(Number(formData.payment.cashAmount || 0) + Number(formData.payment.onlineAmount || 0)).toFixed(2)}
                </p>
              </div>
            )}

            {/* Pricing Summary */}
            <div className="bg-green-50 p-4 rounded-lg">
              <p><strong>Original Amount:</strong> ₹{table?.totalAmount?.toFixed(2) || 0}</p>
              {Number(formData.discount.amount) > 0 && !formData.isMc && (
                <p className="text-green-600">
                  <strong>Discount:</strong> - ₹{
                    formData.discount.type === 'percentage'
                      ? ((table?.totalAmount || 0) * Number(formData.discount.amount) / 100).toFixed(2)
                      : Number(formData.discount.amount).toFixed(2)
                  }
                </p>
              )}
              <p className="text-xl font-bold mt-2">
                <strong>Final Amount:</strong> ₹{formData.isMc ? '0.00' : formData.pricing.total.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onHide}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <FiSave />
                    Submit Update Request
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TableBillUpdateModal;
